import { useState } from 'react'
import { useCompanies, useCreateTask, useDirectory, useProjects } from '../api/hooks'
import { fromDateInputValue } from '../lib/dates'
import { Button, ErrorNotice } from './ui'

/**
 * One-line task capture. Anything not filled in can be set later in the drawer —
 * the point is that adding a task never becomes a form-filling exercise.
 */
export function QuickAdd({
  defaultProjectId,
  defaultCompanyId,
  defaultAssigneeId,
  placeholder = 'Add a task…',
}: {
  defaultProjectId?: string
  defaultCompanyId?: string
  defaultAssigneeId?: string
  placeholder?: string
}) {
  const createTask = useCreateTask()
  const { data: projects } = useProjects()
  const { data: companies } = useCompanies()
  const { data: people } = useDirectory()

  const [title, setTitle] = useState('')
  const [expanded, setExpanded] = useState(false)
  const [projectId, setProjectId] = useState(defaultProjectId ?? '')
  const [companyId, setCompanyId] = useState(defaultCompanyId ?? '')
  const [assigneeId, setAssigneeId] = useState(defaultAssigneeId ?? '')
  const [dueDate, setDueDate] = useState('')

  // A task must land somewhere: a project, or failing that a company.
  const target = defaultProjectId
    ? { projectId: defaultProjectId }
    : projectId
      ? { projectId }
      : companyId
        ? { companyId }
        : defaultCompanyId
          ? { companyId: defaultCompanyId }
          : null

  const canSubmit = title.trim().length > 0 && target !== null

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!canSubmit || !target) return

    createTask.mutate(
      {
        title: title.trim(),
        ...target,
        assigneeId: assigneeId || null,
        dueDate: fromDateInputValue(dueDate),
      },
      {
        onSuccess: () => {
          setTitle('')
          setDueDate('')
          // Keep project/assignee so adding several in a row is quick.
        },
      },
    )
  }

  const needsTarget = title.trim().length > 0 && target === null

  return (
    <form onSubmit={handleSubmit} className="card mb-5 p-3">
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          className="field"
          placeholder={placeholder}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onFocus={() => setExpanded(true)}
        />
        <Button
          type="submit"
          variant="primary"
          loading={createTask.isPending}
          disabled={!canSubmit}
          className="shrink-0"
        >
          Add task
        </Button>
      </div>

      {(expanded || needsTarget) && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {!defaultProjectId && (
            <select
              className="field"
              value={projectId}
              onChange={(e) => {
                setProjectId(e.target.value)
                if (e.target.value) setCompanyId('')
              }}
              aria-label="Project"
            >
              <option value="">No project</option>
              {projects?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.company.shortName ?? p.company.name} › {p.name}
                </option>
              ))}
            </select>
          )}

          {!defaultProjectId && !projectId && !defaultCompanyId && (
            <select
              className="field"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              aria-label="Company"
            >
              <option value="">Choose a company…</option>
              {companies?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}

          <select
            className="field"
            value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
            aria-label="Assignee"
          >
            <option value="">Unassigned</option>
            {people?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>

          <input
            type="date"
            className="field"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            aria-label="Due date"
          />
        </div>
      )}

      {needsTarget && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
          Pick a project or a company so this task has a home.
        </p>
      )}

      {createTask.error && (
        <div className="mt-2">
          <ErrorNotice error={createTask.error} />
        </div>
      )}
    </form>
  )
}
